import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { numberFormatter } from 'core/helpers/formatters';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { policyTypes } from '@ourbranch/policy-types';
import useStyles from './cross-sell-eligibility-notification.styles';

const calculateBundleSavings = (offer) => {
  const options = offer?.options || [];
  const bundleOption = options.find((o) => o.name === 'Bundle');
  const autoOption = options.find((o) => o.type === 'A');
  const homeOption = options.find((o) => o.type === 'H');

  if (!bundleOption) {
    return 0;
  }

  const monoHomeTotal = homeOption?.homeTotal || 0;
  const monoAutoTotal = autoOption?.autoTotal || 0;
  const bundleHomeTotal = bundleOption?.homeTotal || 0;
  const bundleAutoTotal = bundleOption?.autoTotal || 0;

  const homeSavings = monoHomeTotal > bundleHomeTotal ? monoHomeTotal - bundleHomeTotal : 0;
  const rentersTotal = bundleOption?.autoCoverages?.find((coverage) => coverage.type === 'renters_total')?.amount || 0;
  const autoSavings =
    monoAutoTotal > bundleAutoTotal - rentersTotal ? (monoAutoTotal - (bundleAutoTotal - rentersTotal)) * 2 : 0;

  return homeSavings + autoSavings;
};

const money = (value) => `$${numberFormatter(value)}`;

const CrossSellEligibilityNotification = () => {
  const classes = useStyles();
  const {
    account: {
      policies: { list }
    },
    quote: { setPrefillData }
  } = useStore();

  const policies = list || [];
  const eligibleActivePolicies = useMemo(() => {
    return policies.filter(
      (policy) =>
        getPolicyStatus(policy) === PolicyStatus.Active &&
        [policyTypes.Home, policyTypes.Auto].includes(policy.policyType)
    );
  }, [policies]);

  const existingPolicy = eligibleActivePolicies.length === 1 ? eligibleActivePolicies[0] : null;

  const existing = existingPolicy?.policyType === policyTypes.Auto ? 'Auto' : 'Home';
  const newPolicy = existingPolicy?.policyType === policyTypes.Auto ? 'Home' : 'Auto';
  // "an Auto policy", "a Home policy" (#37789)
  const newPolicyArticle = newPolicy === 'Auto' ? 'an' : 'a';
  const savings = useMemo(() => (existingPolicy ? calculateBundleSavings(existingPolicy.offer) : 0), [existingPolicy]);

  const handleBundleNowClick = useCallback(() => {
    if (existingPolicy?.offer) {
      setPrefillData(existingPolicy.offer);
    }
    return '/quote';
  }, [existingPolicy, setPrefillData]);

  if (!existingPolicy) {
    return null;
  }

  if (!savings) {
    return (
      <Label className={classes.warning}>
        This member is probably eligible to add {newPolicyArticle} {newPolicy} policy to their existing {existing}{' '}
        policy. When this member bought their current policy, we did not have enough information to price the{' '}
        {newPolicy} policy.
        <Link to={handleBundleNowClick} className={classes.link}>
          <span> Bundle Now! </span>
        </Link>
      </Label>
    );
  }

  return (
    <Label className={classes.warning}>
      This member is eligible to add {newPolicyArticle} {newPolicy} policy to their existing {existing} policy. Based on
      the member&apos;s most recent offer before they purchased, this would save approximately {money(savings)} per
      year.{' '}
      <Link to={handleBundleNowClick} className={classes.link}>
        <span> Bundle Now! </span>
      </Link>
    </Label>
  );
};

export default CrossSellEligibilityNotification;
